import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import SectionHeading from "../components/SectionHeading";
import { FadeUp } from "../components/motion";
import { fetchCategories, fetchProducts } from "../lib/api";

const SEGMENTS = [
  { v: "", l: "All" },
  { v: "B2C", l: "Homeowners" },
  { v: "B2B", l: "Developers" },
];

export default function Products() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const segment = params.get("segment") || "";
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    fetchProducts({ category: category || undefined, segment: segment || undefined })
      .then(setProducts)
      .catch(() => {});
  }, [category, segment]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const catName = useMemo(
    () => categories.find((c) => c.slug === category)?.name || "All Collections",
    [categories, category]
  );

  return (
    <PageWrapper>
      {/* Hero */}
      <section className="relative pt-[76px]" data-testid="products-hero">
        <div className="relative h-[340px] md:h-[420px] overflow-hidden">
          <img src="/images/gen/hero-products.jpg" alt="NUVUN product collection" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/55 to-[#0A0908]/30" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 pb-10 w-full">
              <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">The Collection</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EDE3]">
                Six ways to own <span className="italic text-[#F2EDE3]/75">the future.</span>
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Category tiles */}
      <section className="py-14" data-testid="products-categories">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {categories.map((c, i) => (
              <FadeUp key={c.slug} delay={i * 0.05}>
                <Link
                  to={`/products/category/${c.slug}`}
                  data-testid={`category-tile-${c.slug}`}
                  className="group relative block overflow-hidden rounded-md aspect-[4/5] gold-frame"
                >
                  <img src={c.hero_image} alt={c.name} loading="lazy" className="product-card-img absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/90 via-[#0A0908]/25 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <div className="font-condensed text-[#D9A441]/80 text-sm">{c.num}</div>
                    <div className="font-body font-extrabold text-xs tracking-[0.08em] uppercase text-[#F2EDE3] mt-1">{c.name}</div>
                    <div className="text-[10px] text-[#F2EDE3]/55 mt-1">{c.product_count} models</div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Filters + grid */}
      <section className="pb-24" data-testid="products-grid-section">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="sticky top-[76px] z-30 bg-[#0A0908]/92 backdrop-blur-lg border-y border-[#D9A441]/15 py-4 flex flex-wrap items-center gap-3 -mx-5 md:-mx-10 lg:-mx-14 px-5 md:px-10 lg:px-14">
            <span className="eyebrow text-[#F2EDE3]/50 mr-2">Filter</span>
            <button
              onClick={() => setFilter("category", "")}
              data-testid="filter-category-all"
              className={`eyebrow px-4 py-2 rounded-sm border transition-colors ${!category ? "border-[#D9A441] text-[#D9A441]" : "border-white/15 text-[#F2EDE3]/60 hover:border-white/40"}`}
            >
              All Types
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => setFilter("category", c.slug)}
                data-testid={`filter-category-${c.slug}`}
                className={`eyebrow px-4 py-2 rounded-sm border transition-colors ${category === c.slug ? "border-[#D9A441] text-[#D9A441]" : "border-white/15 text-[#F2EDE3]/60 hover:border-white/40"}`}
              >
                {c.name}
              </button>
            ))}
            <span className="hidden md:block w-px h-5 bg-white/15 mx-2" />
            {SEGMENTS.map((s) => (
              <button
                key={s.v || "all"}
                onClick={() => setFilter("segment", s.v)}
                data-testid={`filter-segment-${s.v || "all"}`}
                className={`eyebrow px-4 py-2 rounded-sm border transition-colors ${segment === s.v ? "border-[#D9A441] text-[#D9A441]" : "border-white/15 text-[#F2EDE3]/60 hover:border-white/40"}`}
              >
                {s.l}
              </button>
            ))}
          </div>

          <div className="mt-10 flex items-baseline justify-between">
            <h2 className="font-display text-2xl md:text-3xl text-[#F2EDE3]">{catName}</h2>
            <span className="text-xs text-[#F2EDE3]/45" data-testid="products-count">{products.length} models</span>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p, i) => (
              <FadeUp key={p.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={`/products/${p.slug}`}
                  data-testid={`product-card-${p.slug}`}
                  className="group block panel overflow-hidden hover:border-[#D9A441]/50 transition-colors duration-500"
                >
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img src={p.image} alt={p.name} loading="lazy" className="product-card-img absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/70 to-transparent" />
                    <span className="absolute top-4 left-4 eyebrow !text-[9px] bg-[#0A0908]/70 backdrop-blur-sm border border-[#D9A441]/40 text-[#D9A441] px-3 py-1.5 rounded-sm">
                      {p.code}
                    </span>
                    <span className="absolute bottom-4 right-4 eyebrow !text-[9px] bg-[#D9A441] text-[#0A0908] px-3 py-1.5 rounded-sm">
                      {p.size_range}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-display text-xl text-[#F2EDE3] group-hover:text-[#D9A441] transition-colors">{p.name}</div>
                        <div className="text-xs text-[#F2EDE3]/50 mt-1.5">{p.form} · {p.deployment}</div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-[#D9A441] shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#D9A441]/12 flex items-center justify-between text-xs">
                      <span className="text-[#F2EDE3]/45">{p.use_cases[0]}</span>
                      <span className="text-[#D9A441] font-semibold">{p.price_range}</span>
                    </div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>

          {/* Catalog CTA */}
          <FadeUp className="mt-16 panel px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="eyebrow text-[#D9A441] mb-2">Full Specifications</div>
              <div className="font-display text-2xl md:text-3xl text-[#F2EDE3]">Get the complete NUVUN catalogue.</div>
            </div>
            <Link to="/resources" data-testid="products-catalog-cta" className="btn-gold whitespace-nowrap">
              Request Catalogue <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
}
